/**
 * Alshallal Pastry Company HR System - Employee Details Page
 * Phase 2: View employee with masked sensitive data and reveal functionality
 * Phase 14: Added Documents tab for HR fast access
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { RequestStatistics } from '@/components/requests/RequestStatistics';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  User,
  Briefcase,
  CreditCard,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  Loader2,
  AlertCircle,
  RefreshCw,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { listReturnPath } from '../../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import EmployeeDocumentsTab from '@/components/employees/EmployeeDocumentsTab';
import EmployeeQCViolationsTab from '@/components/employees/EmployeeQCViolationsTab';
import { LeaveBenefitsTab } from '@/components/employees/benefits/LeaveBenefitsTab';
import { UnexcusedAbsenceList } from '@/components/leave/UnexcusedAbsences';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  on_leave: 'bg-blue-100 text-blue-800',
  suspended: 'bg-amber-100 text-amber-800',
  terminated: 'bg-red-100 text-red-800',
  resigned: 'bg-slate-100 text-slate-800',
};

const STATUS_LABELS = {
  active: { en: 'Active', ar: 'نشط' },
  on_leave: { en: 'On Leave', ar: 'في إجازة' },
  suspended: { en: 'Suspended', ar: 'موقوف' },
  terminated: { en: 'Terminated', ar: 'منتهي' },
  resigned: { en: 'Resigned', ar: 'مستقيل' },
};

const NATIONALITY_LABELS = {
  saudi: { en: 'Saudi', ar: 'سعودي' },
  non_saudi: { en: 'Non-Saudi', ar: 'غير سعودي' },
};

const EmployeeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, hasRole, user } = useAuth();
  const { hasPermission } = usePermission();
  const { t, language, isRTL } = useLanguage();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state - default to 'details', but check URL param for 'documents'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');

  // Reveal sensitive data
  const [revealedFields, setRevealedFields] = useState({});
  const [revealDialog, setRevealDialog] = useState({ open: false, field: null });
  const [revealReason, setRevealReason] = useState('');
  const [revealing, setRevealing] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Annual leave (2026-09-17): the dates go to the employee, their line manager
  // and HR; the day counts come back only for HR, decided by the backend.
  const [annualStatus, setAnnualStatus] = useState(null);

  const canEdit = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const canDelete = hasRole('hr_admin', 'super_admin');
  const canReveal = hasRole('hr_admin', 'super_admin');
  const canViewDocuments = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');  // Phase 14
  const canUseOCR = hasRole('hr_manager', 'hr_admin', 'super_admin');  // Phase 15
  const canViewQC = hasRole('hr_manager', 'hr_admin', 'super_admin', 'hr_ops', 'hr_er', 'hr_aud');
  // 2026-11: managers' unexcused absence reports - HR only (leave_view_all on the server).
  const canViewAbsenceReports = hasPermission(Permission.LEAVE_VIEW_ALL);

  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(response.data);
    } catch (err) {
      console.error('Failed to fetch employee:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load employee');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/leave/annual-status/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { if (!cancelled) setAnnualStatus(res.data); })
      .catch(() => { if (!cancelled) setAnnualStatus(null); });
    return () => { cancelled = true; };
  }, [id, token]);

  const handleReveal = async () => {
    if (revealReason.length < 10) return;

    try {
      setRevealing(true);
      // Phase 7: Use new reveal-pii endpoint with proper audit logging
      const response = await axios.post(
        `${API_URL}/employees/${id}/reveal-pii`,
        {
          fields: [revealDialog.field],
          reason: revealReason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Extract revealed value from new response format
      const revealedValue = response.data.revealed_fields?.[revealDialog.field];
      if (revealedValue) {
        setRevealedFields(prev => ({
          ...prev,
          [revealDialog.field]: revealedValue
        }));
      }
      setRevealDialog({ open: false, field: null });
      setRevealReason('');
    } catch (err) {
      console.error('Failed to reveal field:', err);
      setError(err.response?.data?.detail?.error || err.response?.data?.detail?.[0]?.msg || 'Failed to reveal data');
    } finally {
      setRevealing(false);
    }
  };

  const handleDelete = async () => {
    if (deleteReason.length < 10) return;

    try {
      setDeleting(true);
      await axios.delete(
        `${API_URL}/employees/${id}?reason=${encodeURIComponent(deleteReason)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(listReturnPath('employees', '/employees'));
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.detail?.error || 'Delete failed');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  const getNationalityLabel = (nat) => {
    const labels = NATIONALITY_LABELS[nat];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : nat;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const renderSensitiveField = (label, maskedValue, fieldKey) => {
    const isRevealed = !!revealedFields[fieldKey];
    const displayValue = isRevealed ? revealedFields[fieldKey] : (maskedValue || '-');

    return (
      <div className="flex items-center justify-between py-2">
        <span className="text-slate-600">{label}</span>
        <div className="flex items-center gap-2">
          <span className={cn(
            "font-medium font-mono",
            isRevealed ? "text-slate-900" : "text-slate-500"
          )}>
            {displayValue}
          </span>
          {canReveal && maskedValue && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (isRevealed) {
                  setRevealedFields(prev => {
                    const newState = { ...prev };
                    delete newState[fieldKey];
                    return newState;
                  });
                } else {
                  setRevealDialog({ open: true, field: fieldKey });
                }
              }}
              className="h-7 w-7 p-0"
              data-testid={`reveal-${fieldKey}`}
            >
              {isRevealed ? (
                <EyeOff className="h-4 w-4 text-slate-400" />
              ) : (
                <Eye className="h-4 w-4 text-slate-400" />
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderField = (label, value) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-slate-600">{label}</span>
      <span className="font-medium text-slate-900">{value || '-'}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !employee) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {isRTL ? 'خطأ' : 'Error'}
            </h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate(listReturnPath('employees', '/employees'))}>
              {isRTL ? 'العودة للموظفين' : 'Back to Employees'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!employee) return null;

  const fullName = isRTL && employee.personal?.first_name_ar
    ? `${employee.personal.first_name_ar} ${employee.personal.middle_name_ar || ''} ${employee.personal.last_name_ar || ''}`.trim()
    : `${employee.personal?.first_name || ''} ${employee.personal?.middle_name || ''} ${employee.personal?.last_name || ''}`.trim();

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="employee-details-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 min-w-0">
        <div className="flex items-center gap-4 min-w-0 max-w-full">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(listReturnPath('employees', '/employees'))}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              <h1 className="text-2xl font-bold text-slate-900 break-words min-w-0" data-testid="employee-name">{fullName}</h1>
              <Badge className={STATUS_STYLES[employee.employment?.status] || STATUS_STYLES.active}>
                {getStatusLabel(employee.employment?.status)}
              </Badge>
            </div>
            <p className="text-slate-600 mt-1">
              {employee.employment?.job_title} • {employee.employment?.department}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/employees/${id}/edit`)}
              data-testid="edit-btn"
            >
              <Edit className="h-4 w-4 me-2" />
              {t('edit')}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="destructive"
              onClick={() => setDeleteDialog(true)}
              data-testid="delete-btn"
            >
              <Trash2 className="h-4 w-4 me-2" />
              {t('delete')}
            </Button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Phase 14: Tabs for Details and Documents */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex flex-wrap h-auto gap-1 justify-start min-w-0 w-full">
          <TabsTrigger value="request-statistics" data-testid="employee-request-statistics-tab">Request statistics</TabsTrigger>
          <TabsTrigger value="leave-benefits" data-testid="employee-leave-benefits-tab">Leave & Benefits History</TabsTrigger>
          <TabsTrigger value="details" className="gap-2">
            <User className="h-4 w-4" />
            {isRTL ? 'التفاصيل' : 'Details'}
          </TabsTrigger>
          {canViewDocuments && (
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="h-4 w-4" />
              {isRTL ? 'المستندات' : 'Documents'}
            </TabsTrigger>
          )}
          {canViewQC && (
            <TabsTrigger value="qc-violations" className="gap-2" data-testid="employee-qc-tab-trigger">
              <AlertTriangle className="h-4 w-4" />
              {isRTL ? 'مخالفات الجودة' : 'QC Violations'}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="leave-benefits" className="space-y-6">
          {canViewAbsenceReports && <UnexcusedAbsenceList isRTL={isRTL} employeeId={id} />}
          <LeaveBenefitsTab employeeId={id} />
        </TabsContent>
        <TabsContent value="request-statistics"><RequestStatistics employeeId={id} prefix="employee" /></TabsContent>
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-slate-600" />
                  {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {renderField(isRTL ? 'الاسم الأول' : 'First Name', employee.personal?.first_name)}
                {employee.personal?.first_name_ar && renderField(isRTL ? 'الاسم الأول (عربي)' : 'First Name (AR)', employee.personal.first_name_ar)}
            {renderField(isRTL ? 'اسم العائلة' : 'Last Name', employee.personal?.last_name)}
            {employee.personal?.last_name_ar && renderField(isRTL ? 'اسم العائلة (عربي)' : 'Last Name (AR)', employee.personal.last_name_ar)}
            <Separator className="my-2" />
            {renderField(isRTL ? 'الجنسية' : 'Nationality', getNationalityLabel(employee.personal?.nationality))}
            {renderField(isRTL ? 'تاريخ الميلاد' : 'Date of Birth', formatDate(employee.personal?.date_of_birth))}
            {renderField(isRTL ? 'الجنس' : 'Gender', employee.personal?.gender)}
            <Separator className="my-2" />
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {isRTL ? 'الجوال' : 'Mobile'}
              </span>
              <span className="font-medium text-slate-900">{employee.personal?.phone || '-'}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {isRTL ? 'البريد' : 'Email'}
              </span>
              <span className="font-medium text-slate-900">{employee.personal?.personal_email || '-'}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {isRTL ? 'المدينة' : 'City'}
              </span>
              <span className="font-medium text-slate-900">{employee.personal?.city || '-'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Identity Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-slate-600" />
              {isRTL ? 'وثائق الهوية' : 'Identity Documents'}
            </CardTitle>
            {canReveal && (
              <CardDescription className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {isRTL ? 'انقر على العين للكشف (يتطلب سبب ويتم تسجيله)' : 'Click eye to reveal (requires reason, logged)'}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-1">
            {renderSensitiveField(
              isRTL ? 'الهوية الوطنية' : 'National ID',
              employee.identity?.national_id_masked,
              'national_id'
            )}
            {renderField(isRTL ? 'انتهاء الهوية' : 'National ID Expiry', formatDate(employee.identity?.national_id_expiry))}
            <Separator className="my-2" />
            {renderSensitiveField(
              isRTL ? 'الإقامة' : 'Iqama',
              employee.identity?.iqama_masked,
              'iqama'
            )}
            {renderField(isRTL ? 'انتهاء الإقامة' : 'Iqama Expiry', formatDate(employee.identity?.iqama_expiry))}
            <Separator className="my-2" />
            {renderField(isRTL ? 'جواز السفر' : 'Passport', employee.identity?.passport_number)}
            {renderField(isRTL ? 'انتهاء الجواز' : 'Passport Expiry', formatDate(employee.identity?.passport_expiry))}
          </CardContent>
        </Card>

        {/* Employment Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-slate-600" />
              {isRTL ? 'معلومات التوظيف' : 'Employment Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {renderField(isRTL ? 'رقم الموظف' : 'Employee Number', employee.employment?.employee_number)}
            {renderField(isRTL ? 'المسمى الوظيفي' : 'Job Title', employee.employment?.job_title)}
            {employee.employment?.job_title_ar && renderField(isRTL ? 'المسمى (عربي)' : 'Job Title (AR)', employee.employment.job_title_ar)}
            <Separator className="my-2" />
            {renderField(isRTL ? 'القسم' : 'Department', employee.employment?.department)}
            {employee.employment?.department_ar && renderField(isRTL ? 'القسم (عربي)' : 'Department (AR)', employee.employment.department_ar)}
            {renderField(isRTL ? 'الموقع' : 'Location', employee.employment?.location)}
            <Separator className="my-2" />
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {isRTL ? 'تاريخ التعيين' : 'Hire Date'}
              </span>
              <span className="font-medium text-slate-900">{formatDate(employee.employment?.hire_date)}</span>
            </div>
            {renderField(isRTL ? 'نوع العقد' : 'Contract Type', employee.employment?.contract_type)}
            {renderField(isRTL ? 'بداية العقد' : 'Contract Start', formatDate(employee.employment?.contract_start_date))}
            {renderField(isRTL ? 'نهاية العقد' : 'Contract End', formatDate(employee.employment?.contract_end_date))}

            {/* Annual leave (2026-09-17) */}
            {annualStatus && (
              <div data-testid="annual-leave-summary">
                <Separator className="my-2" />
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 py-1">
                  {isRTL ? 'الإجازة السنوية' : 'Annual Leave'}
                </p>
                {annualStatus.entitlement_days !== undefined && (
                  <>
                    {renderField(
                      isRTL ? 'الاستحقاق السنوي' : 'Annual Entitlement',
                      `${annualStatus.entitlement_days} ${isRTL ? 'يوماً' : 'days'}`
                        + (annualStatus.entitlement_is_set
                          ? ''
                          : (isRTL ? ' (غير محدد — الافتراضي)' : ' (not set — default)'))
                    )}
                    {renderField(
                      isRTL ? 'الرصيد المستحق حتى اليوم' : 'Accrued to date',
                      `${annualStatus.accrued_days} ${isRTL ? 'يوماً' : 'days'}`
                        + (annualStatus.is_capped
                          ? (isRTL ? ' (بلغ الحد الأقصى)' : ' (capped at entitlement)')
                          : '')
                    )}
                    {annualStatus.entitlement_justification && renderField(
                      isRTL ? 'مبرر الاستحقاق' : 'Entitlement Justification',
                      annualStatus.entitlement_justification
                    )}
                  </>
                )}
                {annualStatus.has_ever_taken_annual_leave ? (
                  <>
                    {renderField(
                      isRTL ? 'بداية آخر إجازة سنوية' : 'Last Annual Leave Start',
                      formatDate(annualStatus.last_leave_start)
                    )}
                    {renderField(
                      isRTL ? 'تاريخ المباشرة بعد الإجازة' : 'Rejoin Date',
                      formatDate(annualStatus.last_leave_rejoin)
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500 py-2">
                    {isRTL
                      ? 'لم تُسجَّل إجازة سنوية بعد — يُحتسب الرصيد من تاريخ التعيين.'
                      : 'No annual leave recorded yet — the balance accrues from the hire date.'}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bank Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-slate-600" />
              {isRTL ? 'المعلومات البنكية' : 'Bank Information'}
            </CardTitle>
            {canReveal && employee.bank?.iban_masked && (
              <CardDescription className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {isRTL ? 'الآيبان محمي - انقر للكشف' : 'IBAN protected - click to reveal'}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-1">
            {renderField(isRTL ? 'اسم البنك' : 'Bank Name', employee.bank?.bank_name)}
            {renderSensitiveField(
              isRTL ? 'الآيبان' : 'IBAN',
              employee.bank?.iban_masked,
              'iban'
            )}
            {renderField(isRTL ? 'صاحب الحساب' : 'Account Holder', employee.bank?.account_holder_name)}
          </CardContent>
        </Card>

        {/* Compensation - Always visible for HR Manager, HR Admin, HR PC, Super Admin */}
        {hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'hr_pc', 'super_admin') && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{isRTL ? 'التعويضات' : 'Compensation'}</CardTitle>
              <CardDescription>
                {isRTL ? 'للتخزين فقط - بدون معالجة الرواتب' : 'Storage only - no payroll processing'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">{isRTL ? 'الراتب الأساسي' : 'Basic Salary'}</p>
                  <p className="text-lg font-bold text-slate-900">
                    {employee.compensation?.basic_salary?.toLocaleString() || '—'} <span className="text-sm font-normal">SAR</span>
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">{isRTL ? 'بدل السكن' : 'Housing'}</p>
                  <p className="text-lg font-bold text-slate-900">
                    {employee.compensation?.housing_allowance?.toLocaleString() || '—'} <span className="text-sm font-normal">SAR</span>
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">{isRTL ? 'بدل النقل' : 'Transportation'}</p>
                  <p className="text-lg font-bold text-slate-900">
                    {employee.compensation?.transportation_allowance?.toLocaleString() || '—'} <span className="text-sm font-normal">SAR</span>
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">{isRTL ? 'بدلات أخرى' : 'Other'}</p>
                  <p className="text-lg font-bold text-slate-900">
                    {employee.compensation?.other_allowances?.toLocaleString() || '—'} <span className="text-sm font-normal">SAR</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <span>{isRTL ? 'أنشئ في:' : 'Created:'} {formatDate(employee.created_at)}</span>
              <span>{isRTL ? 'آخر تحديث:' : 'Updated:'} {formatDate(employee.updated_at)}</span>
              {employee.created_by && <span>{isRTL ? 'بواسطة:' : 'By:'} {employee.created_by}</span>}
            </div>
          </CardContent>
        </Card>
          </div>
        </TabsContent>

        {/* Phase 14: Documents Tab */}
        {canViewDocuments && (
          <TabsContent value="documents">
            <EmployeeDocumentsTab 
              employeeId={id} 
              employeeName={fullName}
            />
          </TabsContent>
        )}
        {canViewQC && (
          <TabsContent value="qc-violations">
            <EmployeeQCViolationsTab employeeId={id} />
          </TabsContent>
        )}
      </Tabs>

      {/* Reveal Sensitive Data Dialog */}
      <Dialog open={revealDialog.open} onOpenChange={(open) => !open && setRevealDialog({ open: false, field: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              {isRTL ? 'كشف البيانات الحساسة' : 'Reveal Sensitive Data'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'سيتم تسجيل هذا الوصول في سجل المراجعة. يرجى إدخال سبب الوصول.'
                : 'This access will be logged in the audit trail. Please provide a reason for access.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reveal-reason">
                {isRTL ? 'سبب الوصول (مطلوب)' : 'Reason for access (required)'}
              </Label>
              <Textarea
                id="reveal-reason"
                value={revealReason}
                onChange={(e) => setRevealReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الوصول (10 أحرف على الأقل)...' : 'Enter reason for access (at least 10 characters)...'}
                rows={3}
                data-testid="reveal-reason-input"
              />
              {revealReason.length > 0 && revealReason.length < 10 && (
                <p className="text-sm text-amber-600">
                  {isRTL ? `${10 - revealReason.length} أحرف متبقية` : `${10 - revealReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevealDialog({ open: false, field: null })}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleReveal}
              disabled={revealReason.length < 10 || revealing}
              data-testid="confirm-reveal-btn"
            >
              {revealing ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 me-2" />
              )}
              {isRTL ? 'كشف' : 'Reveal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {isRTL ? 'حذف الموظف' : 'Delete Employee'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? `هل أنت متأكد من حذف ${fullName}؟ سيتم تعطيل سجل الموظف.`
                : `Are you sure you want to delete ${fullName}? The employee record will be deactivated.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-reason">
                {isRTL ? 'سبب الحذف (مطلوب)' : 'Reason for deletion (required)'}
              </Label>
              <Textarea
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الحذف (10 أحرف على الأقل)...' : 'Enter reason for deletion (at least 10 characters)...'}
                rows={3}
                data-testid="delete-reason-input"
              />
              {deleteReason.length > 0 && deleteReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - deleteReason.length} أحرف متبقية` : `${10 - deleteReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteReason.length < 10 || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? t('loading') : t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDetailsPage;
